// src/components/tariff/TariffForm.js
import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  CircularProgress,
  Paper,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';

const schema = yup.object({
  TariffsName: yup.string().required('Tariff name is required'),
  TariffsEnabled: yup.boolean(),
  TariffsType: yup.string().nullable(),
  TariffsPer: yup.string().nullable(),
  TariffsRateDaytime: yup.number().nullable().transform((value) => isNaN(value) ? null : value),
  TariffsRateNighttime: yup.number().nullable().transform((value) => isNaN(value) ? null : value),
  TariffsFixedStartFee: yup.number().nullable().transform((value) => isNaN(value) ? null : value),
  TariffsIdleChargingFee: yup.number().nullable().transform((value) => isNaN(value) ? null : value),
  TariffsIdleApplyAfter: yup.number().nullable().transform((value) => isNaN(value) ? null : value)
}).required();

const tariffTypes = ['Time-based', 'Energy-based'];
const tariffPer = ['kWh', 'minute'];

const TariffForm = ({ tariff, companyId, onSubmit, isLoading }) => {
  const { control, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: tariff ? {
      TariffsName: tariff.TariffsName,
      TariffsEnabled: tariff.TariffsEnabled,
      TariffsType: tariff.TariffsType || '',
      TariffsPer: tariff.TariffsPer || '',
      TariffsRateDaytime: tariff.TariffsRateDaytime || '',
      TariffsRateNighttime: tariff.TariffsRateNighttime || '',
      TariffsFixedStartFee: tariff.TariffsFixedStartFee || '',
      TariffsIdleChargingFee: tariff.TariffsIdleChargingFee || '',
      TariffsIdleApplyAfter: tariff.TariffsIdleApplyAfter || ''
    } : {
      TariffsName: '',
      TariffsEnabled: true,
      TariffsType: '',
      TariffsPer: '',
      TariffsRateDaytime: '',
      TariffsRateNighttime: '',
      TariffsFixedStartFee: '',
      TariffsIdleChargingFee: '',
      TariffsIdleApplyAfter: ''
    }
  });

  const onFormSubmit = (data) => {
    const formData = {
      ...data,
      TariffsCompanyId: companyId,
    };
    
    Object.keys(formData).forEach(key => {
      if (formData[key] === '') {
        formData[key] = null;
      }
    });
    
    onSubmit(formData);
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Grid container spacing={3} direction="column">
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Basic Information</Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="TariffsName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Tariff Name"
                  fullWidth
                  error={!!errors.TariffsName}
                  helperText={errors.TariffsName?.message}
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="TariffsType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel id="tariff-type-label">Tariff Type</InputLabel>
                  <Select
                    {...field}
                    labelId="tariff-type-label"
                    label="Tariff Type"
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {tariffTypes.map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="TariffsPer"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel id="tariff-per-label">Charged Per</InputLabel>
                  <Select
                    {...field}
                    labelId="tariff-per-label"
                    label="Charged Per"
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {tariffPer.map((per) => (
                      <MenuItem key={per} value={per}>{per}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Rates</Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="TariffsRateDaytime"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Daytime Rate"
                  fullWidth
                  type="number"
                  inputProps={{ step: "0.01" }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="TariffsRateNighttime"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Nighttime Rate"
                  fullWidth
                  type="number"
                  inputProps={{ step: "0.01" }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="TariffsFixedStartFee"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Fixed Start Fee"
                  fullWidth
                  type="number"
                  inputProps={{ step: "0.01" }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="TariffsIdleChargingFee"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Idle Charging Fee"
                  fullWidth
                  type="number"
                  inputProps={{ step: "0.01" }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="TariffsIdleApplyAfter"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Idle Apply After (minutes)"
                  fullWidth
                  type="number"
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="TariffsEnabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Tariff Enabled"
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : null}
              >
                {tariff ? 'Update' : 'Create'} Tariff
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default TariffForm;